import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Banknote, RefreshCw, Check, X, AlertCircle, Trash2 } from 'lucide-react';
import {
  getBancoMCPStatus,
  saveBancoMCPToken,
  clearBancoMCPToken,
  listBancoMCPTools,
  listBancoMCPAccounts,
  triggerBancoMCPSync,
  getBancoMCPSyncStatus,
  getBancoMCPSchedulerStatus,
  bancoMCPAuthenticate,
  bancoMCPAuthInfo,
  BancoMCPStatus,
  BancoMCPSyncStatus,
} from '../services/apiService';

export default function BancoMCPPage() {
  const [status, setStatus] = useState<BancoMCPStatus | null>(null);
  const [schedStatus, setSchedStatus] = useState<any>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [expiresInput, setExpiresInput] = useState('');
  const [accessTokenInput, setAccessTokenInput] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const reload = async () => {
    try {
      const [st, ss] = await Promise.all([
        getBancoMCPStatus(),
        getBancoMCPSchedulerStatus().catch(() => null),
      ]);
      setStatus(st);
      setSchedStatus(ss);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar status');
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleSaveToken = async () => {
    if (!urlInput || !urlInput.startsWith('http')) {
      setError('URL inválida. Esperado: https://api.mcp.ai/banco?t=...');
      return;
    }
    setLoading(true);
    try {
      await saveBancoMCPToken(urlInput, expiresInput);
      setUrlInput('');
      setExpiresInput('');
      setInfo('URL salva (encrypted vault).');
      setError('');
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const handleClearToken = async () => {
    if (!window.confirm('Remover token? Vai precisar re-autorizar no Banco MCP.')) return;
    try {
      await clearBancoMCPToken();
      setAccounts([]);
      setTools([]);
      setInfo('Token removido.');
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Erro ao remover');
    }
  };

  const handleListTools = async () => {
    setLoading(true);
    try {
      const r = await listBancoMCPTools();
      setTools(r.tools || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao listar tools');
    } finally {
      setLoading(false);
    }
  };

  const handleListAccounts = async () => {
    setLoading(true);
    try {
      const r = await listBancoMCPAccounts();
      setAccounts(r.accounts || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao listar contas');
    } finally {
      setLoading(false);
    }
  };

  const handleGetAuthInfo = async () => {
    setLoading(true);
    try {
      const r = await bancoMCPAuthInfo();
      setAuthInfo(r.text);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    if (!accessTokenInput || accessTokenInput.length < 10) {
      setError('Access token vazio ou muito curto');
      return;
    }
    setLoading(true);
    try {
      await bancoMCPAuthenticate(accessTokenInput);
      setAccessTokenInput('');
      setInfo('Autenticado! Tente listar contas agora.');
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao autenticar');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    setInfo('Iniciando sync...');
    try {
      const r = await triggerBancoMCPSync(30);
      if (!r.running) {
        setInfo('Sync não iniciado (verifique o token).');
        setSyncing(false);
        return;
      }
      const startedAt = Date.now();
      const MAX_POLL_MS = 5 * 60 * 1000;
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status: BancoMCPSyncStatus = await getBancoMCPSyncStatus();
          if (status.phase === 'sync') {
            setInfo('Sincronizando transações...');
          } else if (status.phase === 'pipeline') {
            setInfo('Classificando pendentes...');
          }
          if (!status.running) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            if (status.phase === 'error') {
              setError(status.error || 'Erro no sync');
              setInfo('');
              setSyncing(false);
              return;
            }
            const res = status.result;
            const parts: string[] = [];
            if (res) {
              parts.push(`${res.accounts ?? 0} contas`);
              parts.push(`${res.txns_new ?? 0} novas txns`);
              parts.push(`${res.txns_dedup ?? 0} dedup`);
              if (res.pipeline_error) {
                parts.push(`aviso pipeline: ${res.pipeline_error}`);
              }
            }
            setInfo(`Sync concluído: ${parts.join(', ')}`);
            setError('');
            setSyncing(false);
            return;
          }
          if (Date.now() - startedAt > MAX_POLL_MS) {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            setError('Timeout: sync não concluiu em 5 minutos');
            setInfo('');
            setSyncing(false);
          }
        } catch (e: any) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setError(e?.response?.data?.detail || 'Erro ao verificar status do sync');
          setInfo('');
          setSyncing(false);
        }
      }, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro no sync');
      setInfo('');
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-2">
        <Banknote className="h-6 w-6 text-green-600" />
        <h1 className="text-2xl font-bold text-gray-900">Banco MCP — Open Finance</h1>
      </div>

      <p className="text-sm text-gray-600">
        Integração com Banco MCP via Open Finance Brasil. Nosso backend atua como MCP
        client (sem LLM no loop). Sync diário automático às 07:00.
      </p>

      {error && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {info && (
        <div className="px-4 py-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded">
          {info}
        </div>
      )}

      {/* Status do token */}
      <div className="border border-gray-200 rounded p-4 space-y-2">
        <h3 className="font-semibold text-gray-900">URL do servidor MCP</h3>
        {!status?.configured ? (
          <p className="text-sm text-gray-600">
            ❌ Não configurado. Cole a URL obtida em <a href="https://banco.mcp.ai/" target="_blank" rel="noreferrer" className="text-blue-600 underline">banco.mcp.ai</a> abaixo.
          </p>
        ) : (
          <div className="text-sm space-y-1">
            <p>
              {status.valid ? (
                <span className="text-green-700 flex items-center gap-1">
                  <Check className="h-4 w-4" /> URL configurada
                </span>
              ) : (
                <span className="text-red-700 flex items-center gap-1">
                  <X className="h-4 w-4" /> Expirada
                </span>
              )}
            </p>
            <p className="text-gray-500">Salvo em: {status.saved_at}</p>
            <p className="text-gray-500">Expira em: {status.expires_at}</p>
          </div>
        )}

        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://api.mcp.ai/banco?t=..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
          />
          <p className="text-xs text-gray-500">
            Obtenha a URL em <a href="https://banco.mcp.ai/" target="_blank" rel="noreferrer" className="text-blue-600 underline">banco.mcp.ai</a> → "Como integrar" → copie o campo "URL do servidor MCP remoto"
          </p>
          <div className="flex gap-2 flex-wrap">
            <input
              type="date"
              value={expiresInput}
              onChange={(e) => setExpiresInput(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm"
              title="Data de expiração (opcional — default 12 meses)"
            />
            <button
              onClick={handleSaveToken}
              disabled={loading || !urlInput}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Salvar URL
            </button>
            {status?.configured && (
              <button
                onClick={handleClearToken}
                className="px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100 flex items-center gap-1"
              >
                <Trash2 className="h-4 w-4" /> Remover
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Autenticação extra (agent token via app.mcp.ai) */}
      {status?.valid && (
        <div className="border border-amber-300 bg-amber-50 rounded p-4 space-y-2">
          <h3 className="font-semibold text-gray-900">Autenticação Agent (passo 2)</h3>
          <p className="text-sm text-gray-700">
            Mesmo com a URL configurada, o Banco MCP exige um segundo passo:
            <strong> access token gerado em app.mcp.ai</strong>.
          </p>
          <ol className="text-sm text-gray-700 list-decimal ml-5 space-y-1">
            <li>
              Abra{' '}
              <a
                href="https://app.mcp.ai/agent-auth?toolkit=tk_pub_openfinance"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                app.mcp.ai/agent-auth?toolkit=tk_pub_openfinance
              </a>{' '}
              em nova aba
            </li>
            <li>Faça login com email</li>
            <li>Copie o access token mostrado</li>
            <li>Cole abaixo e clique Autenticar</li>
          </ol>

          <div className="flex gap-2 mt-2">
            <input
              type="password"
              value={accessTokenInput}
              onChange={(e) => setAccessTokenInput(e.target.value)}
              placeholder="access token de app.mcp.ai"
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-mono"
            />
            <button
              onClick={handleAuthenticate}
              disabled={loading || !accessTokenInput}
              className="px-3 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
            >
              Autenticar
            </button>
            <button
              onClick={handleGetAuthInfo}
              disabled={loading}
              className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
              title="Pega URL de auth do servidor"
            >
              Ver info
            </button>
          </div>
          {authInfo && (
            <pre className="text-xs bg-white p-2 rounded border border-gray-200 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {authInfo}
            </pre>
          )}
        </div>
      )}

      {/* Tools + accounts (apenas se token OK) */}
      {status?.valid && (
        <>
          <div className="border border-gray-200 rounded p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Contas Open Finance</h3>
              <button
                onClick={handleListAccounts}
                disabled={loading}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                Listar contas
              </button>
            </div>
            {accounts.length === 0 ? (
              <p className="text-sm text-gray-500">Clique "Listar contas" pra ver.</p>
            ) : (
              <div className="space-y-1">
                {accounts.map((a, i) => (
                  <div key={i} className="text-sm bg-gray-50 p-2 rounded font-mono">
                    <div><strong>ID:</strong> {a.id || a.account_id}</div>
                    <div><strong>Banco:</strong> {a.institution || a.bank || '?'}</div>
                    <div><strong>Tipo:</strong> {a.type || '?'}</div>
                    {a.balance !== undefined && <div><strong>Saldo:</strong> {a.balance}</div>}
                    <div className="text-xs text-gray-500 mt-1">
                      Pra mapear: adicione tag <code>; mcp-account-id: {a.id || a.account_id}</code>
                      {' '}na conta correspondente em <code>configs.ledger</code>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-blue-200 bg-blue-50 rounded p-4">
            <Link
              to="/banco-mcp/reconciliacao"
              className="text-blue-700 font-medium text-sm hover:underline"
            >
              Reconciliacao detalhada (banco vs journal) →
            </Link>
            <p className="text-xs text-gray-500 mt-1">
              Compare saldos e transacoes do banco com os lancamentos do journal. Detecta divergencias e permite remover duplicatas.
            </p>
          </div>

          <div className="border border-gray-200 rounded p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Sync de transações</h3>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando…' : 'Sync agora'}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Cria entradas em /pendentes pra cada txn nova. Idempotente via tx_id.
            </p>
          </div>

          <div className="border border-gray-200 rounded p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Tools MCP (debug)</h3>
              <button
                onClick={handleListTools}
                disabled={loading}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                Listar tools
              </button>
            </div>
            {tools.length > 0 && (
              <div className="space-y-1 text-xs font-mono bg-gray-50 p-2 rounded max-h-60 overflow-y-auto">
                {tools.map((t, i) => (
                  <div key={i}>
                    <strong>{t.name}</strong> — {t.description || '(sem desc)'}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {schedStatus && (
        <div className="border border-gray-200 rounded p-4 space-y-1 text-sm">
          <h3 className="font-semibold text-gray-900">Scheduler diário</h3>
          <p>Status: {schedStatus.enabled ? '✅ ativo' : '❌ desabilitado'} (cron {schedStatus.cron_hour}:00)</p>
          {schedStatus.last_run_date && (
            <p className="text-gray-500">Última run: {schedStatus.last_run_date}</p>
          )}
          {schedStatus.last_result && (
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
              {JSON.stringify(schedStatus.last_result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
